import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, LinearProgress } from '@mui/material';

const ThinkingBubble = ({ steps }) => {
  const thinkingRef = useRef(null);
  const [dotColorIndex, setDotColorIndex] = useState(0);
  
  // Array of different dot colors to cycle through
  const dotColors = [ '#31511E'];

  useEffect(() => {
    if (thinkingRef.current) {
      thinkingRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Change dot color each time a new step is added
    if (steps.length > 0) {
      setDotColorIndex((prevIndex) => (prevIndex + 1)% dotColors.length);
    }
  }, [steps]);

  // Process step display logic - clean up "data:" prefix if present
  const renderStep = (step) => {
    // Remove 'data: ' prefix if present
    const cleanStep = step.startsWith('data: ') ? step.substring(6) : step;
    return cleanStep;
  };

  // Get only the latest step, or an empty string if no steps
  const latestStep = steps.length > 0 ? steps[steps.length - 1] : "";
  
  // Current dot color
  const currentDotColor = dotColors[dotColorIndex];

  return (
    <div className="message bot">
      <Paper
        elevation={1}
        className="message-bubble thinking-bubble"
        sx={{
          p: 2,
          borderRadius: '18px',
          backgroundColor: (t)=>t.palette.background.paper,
          maxWidth: '80%',
          marginBottom: '8px',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ 
            width: 12, 
            height: 12, 
            borderRadius: '50%', 
            backgroundColor: currentDotColor,
            mr: 1,
            animation: 'pulse 1s infinite ease-in-out',
            transition: 'background-color 0.3s ease'
          }} />
        </Box>
        
        <LinearProgress 
          sx={{ 
            mb: 2, 
            height: 4, 
            borderRadius: 2,
            backgroundColor: 'rgba(0,0,0,0.05)'
          }} 
        />
        
        <Box sx={{ 
          pl: 2, 
          borderLeft: '2px solid #ddd', 
          mt: 1,
          py: 1,
          minHeight: '30px',
          maxHeight: '60px',
          overflowY: 'auto',
          scrollBehavior: 'smooth'
        }}>
          {steps.length > 0 ? (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white',
                animation: 'fadeIn 0.3s ease-in',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                wordBreak: 'break-word',
                backgroundColor: currentDotColor,
                padding: '4px 8px',
                borderRadius: '8px'
              }}
            >
              {renderStep(latestStep)}
            </Typography>
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: currentDotColor,
                padding: '4px 8px',
                borderRadius: '8px'
              }}
            >
              <span className="thinking-dots">initializing</span>
            </Typography>
          )}
        </Box>
        <div ref={thinkingRef} />
      </Paper>
      
      <style jsx global>{`
        @keyframes pulse {
          0% { opacity: 0.4; }
          50% { opacity: 1; }
          100% { opacity: 0.4; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .thinking-dots::after {
          content: '...';
          display: inline-block;
          animation: ellipsis 1.5s infinite;
          width: 20px;
          text-align: left;
        }
        
        @keyframes ellipsis {
          0% { content: '.'; }
          33% { content: '..'; }
          66% { content: '...'; }
          100% { content: '.'; }
        }
      `}</style>
    </div>
  );
};

export default ThinkingBubble;